import { ImagePaths } from '../../lib/imageUrls.js';

export const copilotFoundationsWave12 = {
  id: 27,
  slug: 'copilot-foundations-wave-1-2',
  ogImage: '/og/courses/copilot-foundations-wave-1-2.jpg',
  courseCode: 'COPILOT-FND-001',
  title: 'Microsoft 365 Copilot Foundations (2026)',
  subtitle: 'Review Copilot deployment controls in an Australian context',
  valueProposition: 'Build a practical foundation for reviewing Microsoft 365 Copilot deployment decisions in Australian organisations. Scenario-based exercises examine permissions, Microsoft Purview, agent availability, privacy considerations, and source evidence; the course is not legal advice, compliance certification, or an IRAP assessment.',
  duration: '8 hours',
  students: 'New',
  level: 'Foundational',
  organization: 'EDUC4TE',
  image: ImagePaths.COURSE_1,
  shortDescription: 'COPILOT-FND-001 — Microsoft 365 Copilot foundations covering permissions, information protection, agent governance, and Australian security context for M365 teams.',
  featured: false,
  price: {
    aud: 599,
    currency: 'AUD',
    earlyBird: 449
  },
  stripeConfig: {
    priceId: 'price_1TTdVwKu1nyc322UpJV5dC01',
    productId: 'prod_USYdCb7sUn8TgY'
  },
  snapshot: {
    whoItsFor: [
      'Microsoft 365 administrators enabling Copilot for the first time',
      'IT coordinators preparing tenant governance before Copilot rollout',
      'Security engineers reviewing Copilot deployment controls against current ASD Blueprint guidance',
      'IT managers accountable for Copilot deployment in Australian organisations'
    ],
    accreditation: 'Australian security and privacy frameworks are used as educational references. This course is not an accredited certification, endorsed framework mapping, legal advice, compliance assurance, or IRAP assessment.'
  },
  logistics: {
    classSize: 'Maximum 20 participants for interactive lab exercises',
    requirements: [
      'Basic familiarity with Microsoft 365 administration',
      'No tenant access is required; exercises use fictional scenarios, sample evidence, or instructor demonstrations. Any optional sandbox must be confirmed before the course; production changes are not part of the exercises.',
      'Computer with a modern browser and reliable internet connection',
      'No prior Copilot experience required'
    ]
  },
  overview: 'Microsoft 365 Copilot is a set of changing experiences whose capabilities, data connections, prerequisites, and availability depend on the specific product, licence, tenant, and configuration. This foundational course helps Australian IT practitioners reason about deployment decisions using current first-party Microsoft documentation and Australian privacy and cyber-security sources.\n\nLearners use fictional scenarios, instructor demonstrations, and sample evidence to examine how permissions affect access to Microsoft 365 content; discuss sensitivity labels, documented data loss prevention controls, and SharePoint discovery governance; and compare agent scenarios without changing a production tenant. Exercises distinguish verified information from assumptions and identify the licensing, role, cloud, and service details that an administrator must confirm for their own environment.\n\nAustralian frameworks are used as learning references, not as compliance determinations or endorsed product mappings. The course does not provide legal advice, certify a deployment, promise an IRAP assessment outcome, or establish that a particular tenant meets a security framework. Its eight-hour learner seat time includes scheduled breaks; facilitated learning and practice total 415 minutes.',
  outcomes: [
    'Explain Copilot architecture, grounding, permissions, privacy, experience, and licensing boundaries.',
    'Interpret readiness, licensing, and update-channel evidence to recommend a pilot cohort.',
    'Diagnose SharePoint oversharing and distinguish permission remediation, Restricted Access Control, and Restricted Content Discovery.',
    'Evaluate sensitivity-label and DLP configurations and predict or interpret supported control outcomes.',
    'Compare Copilot Pages and named agent scenarios by storage, data access, entitlement, publishing, ownership, audit, and lifecycle.',
    'Build an evidence plan for Copilot administration, audit, retention, adoption, and operational review.',
    'Apply Australian privacy, ASD Blueprint, Essential Eight, and IRAP sources as decision inputs without claiming compliance or certification.',
    'Present a phased rollout recommendation with evidence, assumptions, decision gates, owners, and review dates.'
  ],
  audience: [
    'Microsoft 365 administrators enabling Copilot for the first time',
    'IT coordinators and project leads managing Copilot rollout',
    'Security engineers reviewing Copilot deployment controls against current ASD Blueprint guidance',
    'Governance and compliance officers assessing Copilot risk',
    'IT managers in Australian government agencies and regulated organisations'
  ],
  prerequisites: [
    'Basic understanding of Microsoft 365 administration (user management, licences)',
    'Familiarity with SharePoint Online and Teams at a user or admin level',
    'No prior Microsoft Copilot experience required',
    'No security certification required'
  ],
  modules: [
    {
      title: 'Module 1: Architecture and readiness',
      duration: '75 minutes',
      topics: [
        'Explain architecture, grounding, user permissions and scoped privacy statements',
        'Distinguish experience, licensing and app prerequisites for an intended workflow',
        'Interpret a synthetic readiness report and corroborate update-channel evidence',
        'Trace supplied permission paths and distinguish effective access from business purpose',
        'Recommend a pilot cohort with evidence, assumptions, owners and review dates'
      ]
    },
    {
      title: 'Module 2: Pre-Deployment Controls',
      duration: '100 minutes',
      topics: [
        'Sensitivity label concepts and scenario-based taxonomy design for Copilot-related content',
        'Review documented Purview DLP controls and their experience-, policy-, and licence-specific limits',
        'Restricted Content Discovery as a selective discovery control, not a permission change or security boundary; Restricted SharePoint Search retirement',
        'SharePoint organisation- and site-level sharing controls and external-sharing governance',
        'Conditional Access context: validate resource, user scope, service dependencies, and licensing; no universal Copilot-only policy recipe'
      ]
    },
    {
      title: 'Module 3: Pages and the Agent Baseline',
      duration: '90 minutes',
      topics: [
        'Copilot Pages: documented storage prerequisites and governance considerations',
        'SharePoint agent scenarios: review documented scope and permission boundaries, then verify availability',
        'Compare documented agent types and identify deployment prerequisites before any tenant change',
        'Discuss agent interaction with sensitivity-labelled content using a fictional scenario',
        'Locate current official guidance for agent-creation controls; no learner configuration'
      ]
    },
    {
      title: 'Module 4: Copilot Control System and Tenant Governance',
      duration: '80 minutes',
      topics: [
        'Source-led review of documented tenant governance controls; instructor demonstration only where current guidance supports it',
        'Identify questions about connected experiences, data access, permissions, roles, and availability for an organisation to verify',
        'Purview audit: inspect sample activity records and note event-coverage, permission, and retention limits',
        'Prepare questions about current cloud-service assessment material for an authorised assessor; do not determine IRAP scope',
        'Record verified facts, assumptions, unknowns, and accountable owners in a governance decision log'
      ]
    },
    {
      title: 'Module 5: Australian Compliance and Deployment Readiness',
      duration: '70 minutes',
      topics: [
        'Use Privacy Act 1988 and APP guidance as context for scenario discussion, not legal advice',
        'Discuss possible Essential Eight relevance; label any course crosswalk an EDUC4TE learning exercise, not an ASD-endorsed mapping',
        'Find current ASD Blueprint guidance and record evidence limits without asserting a tier or compliance result',
        'Complete a scenario readiness scorecard with verified facts, assumptions, gaps, and owner actions',
        'Check current official product and credential sources before including future-capability claims in planning'
      ]
    }
  ],
  instructionDuration: '415 minutes',
  format: {
    type: 'Live instructor-led one-day workshop (8 hours total seat time including breaks), self-paced online modules, or 8 x 60-minute lunch-and-learn seat-time sessions over 8 days',
    includes: [
      '480 minutes of learner seat time, including 415 minutes of facilitated learning and practice plus 65 minutes of scheduled breaks',
      'Scenario-based exercises using fictional data, an isolated sandbox, or instructor-provided screenshots; no production tenant changes',
      'Scenario-based pre-deployment controls checklist (PDF)',
      'Source-check worksheet for Australian security and privacy guidance; no endorsed tier mapping',
      'Access to recorded sessions for 6 months',
      'Certificate of completion',
      'Email support for 30 days post-course',
      'Links to Copilot Waves 1 & 2 free ebook download'
    ]
  },
  nextIntake: {
    date: 'Register your interest — we will confirm the next cohort',
    details:     'Live cohort delivery available on request for teams of 5 or more; one-day and eight-session agendas are distinct delivery formats'
  },
  faqs: [
    {
      question: 'Do I need prior Microsoft Copilot experience to take this course?',
      answer: 'No prior Copilot experience is required. The course begins with Copilot architecture fundamentals and builds from there. You will need basic Microsoft 365 admin familiarity — user management and licencing — but no Copilot background is assumed.'
    },
    {
      question: 'Is this course relevant for Australian Government agencies?',
      answer: 'The course uses current ASD Blueprint, ACSC Essential Eight, Privacy Act, and OAIC material as educational references. It does not establish an agency deployment’s compliance, assert an ASD Blueprint tier, or determine IRAP assessment scope; agencies should confirm requirements with their authorised security and privacy advisers.'
    },
    {
      question: 'Is this course current to the latest Copilot releases?',
      answer: 'Product names, capabilities, licensing, and tenant availability change. The course uses dated first-party source checks and asks participants to confirm current requirements for the exact experience and tenant before planning a deployment; it does not promise availability of every feature mentioned in older material.'
    },
    {
      question: 'Do I need access to a Microsoft 365 tenant to complete the hands-on exercises?',
      answer: 'No production tenant is required. Exercises use fictional scenarios, sample evidence, and instructor demonstrations; an isolated sandbox may be used only when its cloud, licence, role, and feature availability have been confirmed. Do not change production settings for this course.'
    },
    {
      question: 'Can we enrol the whole IT team?',
      answer: 'Yes. Group enrolment is available; for teams of 5 or more, contact us for a private quote. Training the full IT team can build shared understanding of deployment decisions and governance questions. In-house delivery is also available.'
    },
    {
      question: 'How does this course relate to the free Copilot Waves 1 & 2 ebook?',
      answer: 'The free ebook provides a strategic overview of Wave 1 and Wave 2 for IT leaders. This course adds source-led control analysis, scenario exercises, and governance decision practice. The ebook is a useful pre-reading companion available at educ4te.com/resources.'
    },
    {
      question: 'Will I receive a certificate?',
      answer: 'Yes. Upon completion you will receive an EDUC4TE Certificate of Completion as a record of course participation. It is not a Microsoft credential, accredited certification, compliance assurance, or IRAP assessment result.'
    },
    {
      question: 'What comes after this course?',
      answer: 'The next course in the Microsoft 365 Copilot Australian Deployment Pathway covers later Copilot and agent-governance topics. Product names, release state, licensing, and availability should be rechecked against current Microsoft sources before a cohort.'
    },
    {
      question: 'Can this course be delivered as lunch-and-learn sessions?',
      answer: 'Yes. The alternate format uses eight 60-minute scheduled seat-time sessions over eight days: 415 minutes of facilitated learning and practice plus 65 minutes of break time in total. This is distinct from the one-day agenda, whose 65 break minutes are a 45-minute lunch and two 10-minute breaks. Exercises use scenarios and instructor demonstrations rather than production-tenant changes; contact us to arrange session timing.'
    }
  ]
};
